import React, { useState } from "react";
import { ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { castVote, removeVote } from "../lib/api";

/**
 * Single upvote control per PRD § Phase 4:
 *   • ChevronUp icon + tabular tally
 *   • Optimistic UX (increment / decrement immediately; rollback on error)
 *   • Rank is NOT conveyed by colour alone — the numeric tally + rank badge
 *     elsewhere carry the semantics
 *   • Signed-out state renders a disabled control that toasts a sign-in nudge
 *   • Owner sees a plain tally (no button) — self-vote is impossible in UI too
 */
export default function VoteChevron({ submission, viewer, onChange, size = "md" }) {
  const [votes, setVotes] = useState(submission.votes?.up ?? 0);
  const [voted, setVoted] = useState(!!submission.viewer_has_voted);
  const [busy, setBusy] = useState(false);

  const isOwner = viewer && viewer.id === submission.user_id;
  const disabled = busy || isOwner;
  const dims = size === "lg"
    ? { btn: "px-3 py-2 text-body", icon: 22 }
    : { btn: "px-2 py-1 text-meta", icon: 18 };

  // Signed-out — render a passive control that nudges to sign in.
  if (!viewer) {
    return (
      <button
        type="button"
        data-testid="vote-signin-nudge"
        onClick={() => toast("Sign in to upvote a showcase.")}
        className={`inline-flex items-center gap-1 rounded-chip border border-ink-chip bg-ink-raised text-ink-muted hover:text-ink-text ${dims.btn}`}
      >
        <ChevronUp size={dims.icon} />
        <span className="tabular">{votes}</span>
      </button>
    );
  }

  if (isOwner) {
    return (
      <div
        data-testid="vote-owner-tally"
        className={`inline-flex items-center gap-1 rounded-chip border border-ink-chip bg-ink-raised text-ink-muted ${dims.btn}`}
        aria-label={`${votes} upvotes on your entry`}
      >
        <ChevronUp size={dims.icon} />
        <span className="tabular">{votes}</span>
      </div>
    );
  }

  async function toggle() {
    // Optimistic
    const nextVoted = !voted;
    const nextVotes = votes + (nextVoted ? 1 : -1);
    setVoted(nextVoted);
    setVotes(nextVotes);
    setBusy(true);
    try {
      const resp = nextVoted
        ? await castVote(submission.id)
        : await removeVote(submission.id);
      setVotes(resp.votes.up);
      setVoted(!!resp.viewer_has_voted);
      onChange?.({ id: submission.id, votes: resp.votes, viewer_has_voted: !!resp.viewer_has_voted });
    } catch (e) {
      // Rollback
      setVoted(!nextVoted);
      setVotes(votes);
      const msg = e?.response?.data?.detail || e?.message || "Vote failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      data-testid={voted ? "vote-remove-btn" : "vote-cast-btn"}
      onClick={toggle}
      disabled={disabled}
      aria-pressed={voted}
      aria-label={voted ? `Remove upvote (${votes})` : `Upvote (${votes})`}
      className={
        `inline-flex items-center gap-1 rounded-chip border transition-colors duration-cc ease-cc-ease ${dims.btn} ` +
        (voted
          ? "border-indigo-chip-border bg-indigo-chip-bg text-indigo-chip-text"
          : "border-ink-chip bg-ink-raised text-ink-muted hover:border-indigo-chip-border hover:text-ink-text") +
        (busy ? " opacity-60 cursor-progress" : "")
      }
    >
      <ChevronUp size={dims.icon} strokeWidth={voted ? 2.5 : 2} />
      <span className="tabular">{votes}</span>
    </button>
  );
}
